import React from "react";

import AsyncImage from "@appComponents/AsyncImage/AsyncImage";
import noPhotoCompany from "@images/no_photo_company.png";
import { formatNumberToUSD } from "@features/utils/functions";
import { Pitch } from "@services/Pitches.service";
import { WORDPRESS_URL } from "@bundles/consts";
import { Button } from "@appComponents/Button";
import classNames from "classnames";
import OurInvestorsImage from "../icons/our_investors_image.svg";
import ExposureImage from "../icons/exposure_image.svg";
import ChevronDownIcon from "../../../icons/chevron-down-black.svg";
import ConditionalWrapper from "@appComponents/ConditionalWrapper";
import { Tooltip } from "antd";

const pitchDisabledStatuses = ["Closed", "Post process", "Finished"];
const pitchDisplayBtnStatuses = [
  ...pitchDisabledStatuses,
  "Live unlisted",
  "Live listed",
];

export const shareOwnershipText =
  "When you invest in private equity through our platform, you're not buying shares directly in the underlying company—you’re acquiring shares in a Segregated Portfolio (SP) within a segregated structure called an SPC that is backed by shares in the underlying company. This setup gives you indirect ownership, legal asset segregation, and access to private Bitcoin companies. In this section, we explain exactly how it works, the safeguards in place, and what that means for you as an investor.";

export const gettingStartedText = (
  <>
    <p>
      New to private equity investing on the BF platform? This section covers
      everything you need to know before making your first investment—who can
      invest, how to fund your account, what the minimums are, and why deals
      aren’t always live
    </p>

    <p>
      If you're looking for live opportunities and don’t see any listed above,
      don’t worry. We only launch a limited number of deals each year. You can
      always explore the Secondary Market which is open 24/7 where you can
      invest in shares available from other investors.
    </p>
  </>
);

export const howToInvestmentsWorkText = (
  <>
    <p>
      When you invest through BF, you’re not buying shares directly in a
      company—you’re investing through a legally segregated structure called a
      Segregated Portfolio (SP). This section explains what you're actually
      investing in, the difference between A and B shares, and how convertible
      notes work for early-stage deals.
    </p>
    <p>
      Whether you're looking at equity or convertible instruments, this tab
      breaks down the structure, rights, and protections built into each
      investment.
    </p>
  </>
);

export const securityAndRegulationText = (
  <>
    <p>
      Your investments are protected by a multi-jurisdictional regulatory
      framework designed for resilience and transparency. In this section, we
      outline how Bnk To The Future is regulated across jurisdictions, how
      assets are held, and what safeguards are in place—even in the unlikely
      event of platform shutdown.
    </p>

    <p>
      If you're investing through BF, you're doing so through legally segregated
      structures backed by licensed entities and regulatory oversight.
    </p>
  </>
);

export const feesReturnsAndLiquidityText = (
  <>
    <p>
      Private equity is a long-term game, and returns are typically realized
      during a company’s exit—through acquisition, IPO, or a future funding
      round. While some deals may become tradable on our Secondary Market,
      liquidity isn’t guaranteed.
    </p>

    <p>
      In this section, we outline how and when returns might occur, the
      potential for early exits via our Secondary Market, and the fees involved
      in buying, selling, and managing your investments.
    </p>
  </>
);

export const overviewText = (
  <>
    <p>
      <strong>Welcome to the Private Equity Primary Market</strong>
    </p>
    <p className="mb-0">
      The Private Equity Primary Market is where BF users can access both
      current and past investment opportunities launched through the platform.
      BF typically launches one or two new deals each year. You can view all
      Primary Market deals above.
    </p>
  </>
);

export const overviewFaq = [
  {
    title: "What is the Private Equity Primary Market?",
    description: (
      <p className="ml-8">
        The Private Equity Primary Market is where BF users can access both
        current and past investment opportunities launched through the platform.
        BF typically launches one or two new deals each year. You can view all
        Primary Market deals below.
      </p>
    ),
  },
];

export const shareOwnershipContent = (
  <div>
    <p>Understanding the SPC & Investment Structure</p>
    <ul>
      <li>
        <strong>Bnk To The Future Capital SPC</strong> is a Special Purpose
        Company registered in the Cayman Islands, operating under a Segregated
        Portfolio Company (SPC) structure. [Details to be added about
        registration number etc]
      </li>
      <li>
        A <strong>Special Purpose Vehicle (SPV)</strong> has been created to
        hold, on a pass-through basis, the underlying securities of companies
        that raised funds via the platform.
      </li>
      <li>
        <strong>BF Securities Broker does not hold any of these assets.</strong>{" "}
        These are not client assets held by the broker, and are therefore not
        protected as client assets.
      </li>
    </ul>

    <p className="md:mt-[40px] mt-[20px]">Key Things to Know:</p>
    <ul>
      <li>
        <strong>You do not hold direct shares</strong> in the underlying
        companies.
      </li>
      <li>
        Instead, you own{" "}
        <strong>shares in a specific SP (Segregated Portfolio),</strong> which
        mirrors the ownership of the underlying company on a 1:1 basis.
      </li>
      <li>
        This is <strong>not a fund:</strong> each investor directly mirrors the
        asset they are invested in via the SPV structure.
      </li>
      <li>
        SP shares may be acquired via the <strong>primary market</strong> (when
        the deal is first listed) or the <strong>secondary market</strong> (from
        other investors).
      </li>
      <li>
        There may be <strong>information asymmetry</strong> between primary and
        secondary investors. While we aim to provide updated company info,
        investors are responsible for their own due diligence.
      </li>
    </ul>

    <p>
      This is <strong>not investment advice.</strong> Bnk To The Future does not
      provide recommendations on whether to invest or not.
    </p>
  </div>
);

export const shareOwnershipFaq = [
  {
    title: "Disclaimer: Understanding the SPC & Investment Structure",
    description: (
      <div>
        <p className="ml-8">
          Bnk To The Future Capital SPC is a Special Purpose Company registered
          in the Cayman Islands, operating under a Segregated Portfolio Company
          (SPC) structure. Company Number is 298427. The registered address is
          Willow House, Cricket Square, KY1-1001, Cayman Islands.
        </p>
        <p className="ml-8">
          The SPC creates one Segregated Portfolio (SP) per pitch to hold, on a
          pass-through basis, the underlying securities of companies that raised
          funds via the platform.
        </p>
        <p className="ml-8">
          Neither the BF Securities Broker nor the Platform Operator holds any
          of these assets. These are not client assets held by the broker, and
          are therefore not protected as client assets. Instead they are
          segregated.
        </p>
      </div>
    ),
  },
  {
    title: "Key Things to Know",
    description: (
      <div>
        <p className="ml-8">
          The SPC creates a specific SP for each company raising funds on the
          Platform.
        </p>
        <p className="ml-8">
          Investors do not hold direct shares in the underlying companies.
        </p>
        <p className="ml-8">
          Instead, Investors own shares in a specific SP, which mirrors the
          ownership of the underlying company on a 1:1 basis.
        </p>
        <p className="ml-8">
          This is not a fund. Each share that an investor holds in the SPV
          directly mirrors, in quantity and value, the underlying asset held by
          the SPV.
        </p>
        <p className="ml-8">
          SP shares may be acquired via the primary market (when the deal is
          first listed) or the secondary market (from other investors).
        </p>
        <p className="ml-8">
          Information about the Investee Entity may not be equally or adequately
          available to all investors. All investors, whether primary or
          secondary market purchasers, are expected to conduct their own due
          diligence.
        </p>
        <p className="ml-8">
          This is not investment advice. Bnk To The Future does not provide
          recommendations on whether to invest or not.
        </p>
      </div>
    ),
  },
  {
    title: "Asset Protection Through SP Segregation",
    description: (
      <div>
        <p className="ml-8">
          A key feature of the SPC is that the assets and liabilities of each SP
          are segregated from the assets and liabilities of other SPs. This
          means that if one SP faces financial difficulties, the assets in other
          SPs are protected.
        </p>
      </div>
    ),
  },
  {
    title: "Disclaimer",
    description: (
      <div>
        <p className="ml-8">
          The information provided here is for general knowledge and
          informational purposes only. Investing in private equity carries
          significant risk, and investors should conduct their own research and
          consult with a qualified financial advisor before making any
          investment decisions. Please review our risk warnings{" "}
          <a
            href="https://home.bnktothefuture.com/risk-warnings/"
            className="underline font-semibold"
          >
            here.
          </a>
        </p>
      </div>
    ),
  },

  {
    title: "How our investors own indirect shares of Bitcoin companies",
    description: (
      <div>
        <OurInvestorsImage className="scale-90" />
      </div>
    ),
  },
  {
    title: "How you get exposure to private Bitcoin companies (e.g. Kraken)",
    description: (
      <div>
        <ExposureImage className="scale-95" />
      </div>
    ),
  },
];

export const faq = [
  {
    title: "What is Private Equity?",
    description: (
      <p className="ml-8">
        Private equity is capital invested in private companies (not listed on
        public stock exchanges), often to fund growth, acquisitions, or
        restructuring. Investors typically aim to invest long term and sell
        their shares later at a higher valuation either on secondary market or
        in the event they go public.
      </p>
    ),
  },
  {
    title: "Who can invest in private equity?",
    description: (
      <p className="ml-8">
        Investing in private equity is open to Tier 3 members or above.
      </p>
    ),
  },
  {
    title: "What do I need to do before I can invest?",
    description: (
      <>
        <p className="ml-8">You’ll need to:</p>

        <ol>
          <li>
            Become a tier 3 member in the identity section in your account
          </li>
          <li>Fund your account with BTC, ETH, USDT, or fiat</li>
          <li>
            Convert any funds to USD and transfer them to our integrated
            platform broker BF Global IB MU in the funds section of your
            account.
          </li>
          <li>
            Browse any live deals on this page and complete an investment.
          </li>
        </ol>
      </>
    ),
  },
  {
    title: "What’s the minimum investment?",
    description: (
      <p className="ml-8">
        Most offerings start at <strong>$1,000</strong>, though some may require
        higher minimums.
      </p>
    ),
  },
  {
    title: "Which currencies can I use to fund my account?",
    description: (
      <p className="ml-8">
        We support deposits in USDT (Tether), Bitcoin (BTC), ETH and traditional
        bank wires for fiat currencies that you can convert to USD to make an
        investment.
      </p>
    ),
  },
  {
    title: "Can I invest from my country?",
    description: (
      <p className="ml-8">
        We accept investors from many jurisdictions. If there are restrictions
        for your location, we’ll notify you during onboarding when you upgrade
        to Tier 3.
      </p>
    ),
  },
  {
    title: "Why are there no live deals listed above?",
    description: (
      <p className="ml-8">
        We only launch a few exclusive deals each year. The section above shows
        past deals that are now closed and any live deal, if currently
        available. If no primary market deals are listed, you can explore our
        Secondary Market, where existing investors list shares for sale 24/7.
        Just click the “Secondary Market” tab above to browse available
        opportunities.
      </p>
    ),
  },
];

export const howInvestmentWorkFaq = [
  {
    title: "What exactly am I investing in?",
    description: (
      <p className="ml-8">
        You’re investing in a <strong>Segregated Portfolio (SP)</strong>—a
        legally separate entity that holds shares or convertible notes in the
        underlying company. You receive either <strong>A shares</strong> or{" "}
        <strong>B shares</strong> in the SP.
      </p>
    ),
  },
  {
    title: "What’s the difference between A shares and B shares?",
    description: (
      <ul>
        <li>
          <strong>A Shares:</strong> Come with voting rights on key decisions
          within the SP
        </li>
        <li>
          <strong>B Shares:</strong> No voting rights, but provide full
          financial benefits
        </li>
      </ul>
    ),
  },
  {
    title: "How are my shares held?",
    description: (
      <p className="ml-8">
        Your shares are held segregated in the SP, which is managed by{" "}
        <strong>Bnk To The Future Capital SPC</strong> in the Cayman Islands.
        Each SP is legally distinct and isolated from others, so risks don’t
        cross over between investments.
      </p>
    ),
  },
  {
    title: "What is a convertible note?",
    description: (
      <p className="ml-8">
        A convertible note is a short-term debt instrument that converts into
        equity during a future funding round. It’s commonly used for early-stage
        deals where a valuation hasn’t been set.
      </p>
    ),
  },
  {
    title: "Why do some companies raise via convertible notes?",
    description: (
      <p className="ml-8">
        It’s fast, flexible, and defers valuation to a later stage. Investors
        benefit from terms like <strong>discounts</strong> and{" "}
        <strong>valuation caps</strong> during the conversion.
      </p>
    ),
  },
];

export const securityAndRegulationFaq = [
  {
    title: "Is BnkToTheFuture regulated?",
    description: (
      <>
        <p className="ml-8">
          Yes. We operate under a multi-jurisdictional regulatory framework:
        </p>

        <ul>
          <li>
            <strong>Bnk To The Future Capital SPC</strong> (Cayman Islands) –
            Holds investments
          </li>
          <li>
            <strong>BF Global IB MU</strong> (Mauritius) – Licensed Investment
            Dealer registered with the Mauritius FSC.
          </li>
          <li>
            <strong>BF Virtual Assets Ltd</strong> (BVI) – Virtual asset broker
            for custody and crypto trading registered with the BVI FSC.
          </li>
          <li>
            Bnk To The Future (Caynan Islands) - Platform operator registered
            with CIMA.
          </li>
        </ul>

        <p className="ml-8 mt-8">
          Full details are provided when you create and upgrade your account.
        </p>
      </>
    ),
  },
  {
    title: "What happens if BnkToTheFuture shuts down?",
    description: (
      <p className="ml-8">
        Your investments are held in segregated structures and remain in custody
        in your name. In the unlikely event of platform closure, a
        court-appointed liquidator would manage asset distribution.
      </p>
    ),
  },
];

export const feesReturnsAndLiquidityFaq = [
  {
    title: "What returns can I expect?",
    description: (
      <>
        <p className="ml-8">
          Returns vary by deal. They’re usually realized during{" "}
          <strong>an exit event</strong>
          (e.g. acquisition or public offering). Dividends are rare in
          early-stage investing but may be distributed if the company chooses.
          If the company increases in value at a future funding round and is
          progressing, we may list it on our secondary market, where we match
          buyers and sellers on our platform.
        </p>
      </>
    ),
  },
  {
    title: "Can I sell my shares before an exit?",
    description: (
      <>
        <p className="ml-8">
          In some cases, yes. Shares in select companies may be listed on our{" "}
          <strong>Secondary Market</strong>, where verified investors can buy
          and sell based on the last known valuation.
        </p>
      </>
    ),
  },
  {
    title: "Will my investment be listed on the Secondary Market?",
    description: (
      <>
        <p className="ml-8">
          Not all investments are eligible, but we aim to list companies that
          still hold potential and value for trading.
        </p>
      </>
    ),
  },
  {
    title: "What fees apply when I invest?",
    description: (
      <>
        <ul>
          <li>
            <strong>Transaction Fees:</strong> May apply per investment
          </li>
          <li>
            <strong>Exit Fee:</strong> 5% on distributions
          </li>
          <li>
            <strong>Dividend Fee:</strong> 5% on any payouts
          </li>
          <li>
            <strong>Secondary Market Trade Fee:</strong> 2.5% on successful
            buy/sell orders
          </li>
          <li>
            <strong>Corporate Action Fee:</strong> 5% on all corporate actions
          </li>
        </ul>

        <p className="ml-8 mt-8">
          For the full breakdown, visit:{" "}
          <a
            href="https://home.bnktothefuture.com/fees/"
            className="underline font-semibold"
          >
            bnktothefuture.com/fees
          </a>
        </p>
      </>
    ),
  },
];

type GetPitchesColumnsArgs = {
  isMobile: boolean;
  handleExpandToggle: (record: Pitch) => void;
  expandedRowKeys: React.Key[];
};

export const getPitchesColumns = ({
  isMobile,
  handleExpandToggle,
  expandedRowKeys,
}: GetPitchesColumnsArgs) =>
  isMobile
    ? [
        {
          title: "PITCH",
          dataIndex: "title",
          key: "title",
          render: (_, pitch: Pitch) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage image={pitch.image} fallback={noPhotoCompany}>
                  <img alt="logo" className="h-[36px] w-[36px] rounded-[5px]" />
                </AsyncImage>

                <a href={`/pitches/${pitch.slug}`}>
                  <span>{pitch.company_name}</span>
                </a>
              </div>
            );
          },
        },
        {
          title: "",
          dataIndex: "action",
          key: "action",
          width: 10,
          render: (_, pitch: Pitch) => {
            return (
              <Button isSmall onClick={() => handleExpandToggle(pitch)}>
                <span
                  className={classNames({
                    "rotate-180": expandedRowKeys.includes(pitch.id),
                  })}
                >
                  <ChevronDownIcon />
                </span>
              </Button>
            );
          },
        },
      ]
    : [
        {
          title: "PITCH",
          dataIndex: "title",
          key: "title",
          sorter: (a, b) => a.title?.localeCompare(b.title),
          render: (_, pitch: Pitch) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage image={pitch.image} fallback={noPhotoCompany}>
                  <img alt="logo" className="h-[36px] w-[36px] rounded-[5px]" />
                </AsyncImage>

                <a href={`/pitches/${pitch.slug}`}>
                  <span>{pitch.company_name}</span>
                </a>
              </div>
            );
          },
        },
        {
          title: "NO. OF INVESTORS",
          dataIndex: "number_of_investors",
          key: "number_of_investors",
          sorter: (a, b) =>
            Number(a.number_of_investors ?? 0) -
            Number(b.number_of_investors ?? 0),
        },
        {
          title: "RAISED %",
          dataIndex: "raised_percent",
          key: "raised_percent",
          sorter: (a, b) =>
            Number(a.raised_percent ?? 0) - Number(b.raised_percent ?? 0),
          render: (raisedPercent: string) => {
            return (
              <div>
                <div className="text-[#81109C] mb-[7px]">
                  {Number(raisedPercent ?? 0).toFixed(0)} %
                </div>
                <div
                  className="h-[6px] rounded-[10px] bg-[#81109C]"
                  style={{
                    width: `${
                      Number(raisedPercent ?? 0) > 100
                        ? 100
                        : Number(raisedPercent ?? 0)
                    }%`,
                  }}
                ></div>
              </div>
            );
          },
        },
        {
          title: "RAISED $",
          dataIndex: "raised_amount",
          key: "raised_amount",
          align: "right" as const,
          sorter: (a, b) =>
            Number(a.raised_amount ?? 0) - Number(b.raised_amount ?? 0),
          render: (_, pitch: Pitch) => {
            if (
              Number(pitch.raised_amount ?? 0) <
              Number(pitch.expected_amount ?? 0)
            ) {
              return (
                <div>
                  {formatNumberToUSD(Number(pitch.raised_amount ?? 0))}
                  <span className="text-grey-lightest">
                    &nbsp;of{" "}
                    {formatNumberToUSD(Number(pitch.expected_amount ?? 0))}
                  </span>
                </div>
              );
            } else {
              return formatNumberToUSD(Number(pitch.raised_amount ?? 0));
            }
          },
        },
        {
          title: "",
          dataIndex: "action",
          key: "action",
          align: "right" as const,
          render: (_, pitch: Pitch) => {
            const isButtonDisabled = pitchDisabledStatuses.includes(
              pitch.status
            );
            const isButtonVisible = pitchDisplayBtnStatuses.includes(
              pitch.status
            );
            return (
              <div className="flex gap-2 items-center">
                {isButtonVisible && (
                  <ConditionalWrapper
                    condition={isButtonDisabled}
                    wrapper={(childern) => (
                      <Tooltip title="This pitch is no longer open for investment.">
                        {childern}
                      </Tooltip>
                    )}
                  >
                    <Button
                      isSmall
                      disabled={isButtonDisabled}
                      onClick={() =>
                        window.location.replace(`/pitches/${pitch.slug}`)
                      }
                    >
                      {isButtonDisabled ? "Pitch Closed" : "Invest Now"}
                    </Button>
                  </ConditionalWrapper>
                )}

                <Button isSmall onClick={() => handleExpandToggle(pitch)}>
                  <span
                    className={classNames({
                      "rotate-180": expandedRowKeys.includes(pitch.id),
                    })}
                  >
                    <ChevronDownIcon />
                  </span>
                </Button>
              </div>
            );
          },
        },
      ];
